import { Drawer } from "@mui/material";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";
import { Suspense, useContext, useEffect, useRef, useState } from "react";
import { QueryClientProvider } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { createBrowserRouter, Outlet, RouterProvider, useBlocker, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import styled from "styled-components";
import "./App.css";
import AppContext from "./AppContext";
import LayoutContext from "./LayoutContext";
import { AppQueryClient } from "./api/queryClient";
import "./assets/css/Colors.css";
import "./assets/css/UtilityClasses.css";
import { ReactComponent as AlertTriangle } from "./assets/icons/alert-triangle.svg";
import NoInternetMarker from "./components/common/NoInternetMarker";
import PopupManager from "./components/common/PopupManager";
import ChatComponent from "./components/common/chatWindow/ChatComponent";
import ChatWindow from "./components/common/chatWindow/ChatWindow";
import DialogBoxComponent from "./components/common/dialogs/DialogBoxComponent";
import DownloadAndInstallAppDialog from "./components/common/dialogs/WelcomeDialog/DownloadAndInstallAppDialog";
import WelcomeDialog from "./components/common/dialogs/WelcomeDialog/WelcomeDialog";
import WorkspaceLoader from "./components/common/loaders/WorkspaceLoader";
import PreLoader from "./components/common/loaders/PreLoader";
import ControlBar from "./components/common/mainApp/PremiumControlBar";
import Footer from "./components/common/mainApp/Footer";
import Header from "./components/common/mainApp/PremiumHeader";
import NavigationPanel from "./components/common/mainApp/PremiumNavigationPanel";
import ProductsSidePanel from "./components/common/mainApp/ProductsSidePanel";
import FullScreenOverlay from "./components/common/overlay/FullScreenOverlay";
import TermsAndPrivacyPanel from "./components/common/termsandConditions/TermsAndPrivacyPanel";
import MobileNotAvailable from "./components/pages/generic/MobileNotAvailable";
import { PENDING_BILLING_ACTIVATION_TOAST_KEY } from "./config/constant";
import { navigationBar } from "./config/navigationBarConfig";
import { AllRoutes } from "./routes/AllRoutes";
import * as Library from "./utils/Library";
import useUnsavedChangesWarning from "./utils/hooks/useUnsavedChangesWarning";
import { hasUserCompletedOnboarding, verifyUserIsOnboarding } from "./utils/onboardingHelper";
import { setNavigations } from "./utils/redux/navigationAccessSlice";
import { setData } from "./utils/redux/userDataSlice";
import { roleAccesse } from "./utils/roleManager";

// #region SCOPED CSS
const StyledChatDrawerResizeHandle = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    width: 8px;
    cursor: ew-resize;
    touch-action: none;

    &::after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 2px;
        background: transparent;
        transition: background-color 0.2s;
    }

    &:hover::after,
    &:focus-visible::after {
        background: var(--text-icons-blue-500, #00aeef);
    }

    &:focus-visible {
        outline: none;
    }
`;
// #endregion

export default function App() {
    const router = createBrowserRouter([{ element: <AppTemplate />, children: AllRoutes }]);
    return <RouterProvider router={router} />;
}

// #region GLOBALS
const GLOBALS = {
    disableWelcomePopup: true,
    disableDownloadAppPopup: true
};

const CHAT_DRAWER_MIN_WIDTH = 360;
const CHAT_DRAWER_MAX_WIDTH = 600;
const CHAT_DRAWER_KEYBOARD_STEP = 10;
// #endregion

function AppTemplate() {
    // #region STATES
    // ------ HOOKS ------
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigations = useSelector(state => state.navigationAccesses);
    const blocker = useBlocker(() => hasUnsavedChanges && !showUnsavedChangesPopup); // Blocker to stop navigation when there are unsaved changes

    const urlParams = new URLSearchParams(location.search);
    const showPopup1 = urlParams.get("showPopup1") === "true";
    const showPopup2 = urlParams.get("showPopup2") === "true";

    const path = location.pathname;
    if (["development", "testing"].includes(process.env.NODE_ENV)) window.BOLibrary = Library;

    // ------ METHODS FOR STATES ------
    const isPublicAccessPageURL = ["/pre-enrollment-invited", "/pre-enrollment", "/login", "/resetpassword"].some(url =>
        path.startsWith(url)
    );

    // ------ CONTEXT VARIABLES ------
    const {
        getUserDataToken,
        setGetUserDataToken,
        refreshUserData,
        setRefreshUserData,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        isLoadingApp,
        setIsLoadingApp,
        showUnsavedChangesPopup,
        setShowUnsavedChangesPopup,
        setUserData,
        redirectURL,
        setRedirectURL,
        setShowWelcomePopup,
        setShowDownloadAppPopup,
        setIsPwaInstalled,
        isPwaInstalled,
        setFirstLoginDone,
        userData,
        aiChatSidePanelOpen,
        setAiChatIsVisible,
        setAiChatIsDisabled
    } = useContext(AppContext);

    const {
        setShowLogoutPopup,
        layoutType,
        setLayoutType,
        controlBarLeftBtns,
        setControlBarLeftBtns,
        controlBarCenterBtns,
        setControlBarCenterBtns,
        controlBarRigthBtns,
        setControlBarRightBtns,
        isMobile,
        setIsMobile,
        filterHeaderProperties,
        setFilterHeaderProperties
    } = useContext(LayoutContext);

    // ------ USE STATES ------
    const userRole = userData?.role;
    const editAccess = roleAccesse(userRole, "retail brand account admin");
    const [showMasterLayout, setShowMasterLayout] = useState(!isPublicAccessPageURL && path !== "/");
    const [browser, setBrowser] = useState("");
    const [navigationBarMenuBtns, setNavigationBarMenuBtns] = useState([]);
    const [showTAC, setShowTAC] = useState({ TermsAndPrivacyPanel: false, PrivacyPolicy: false });
    const [currentFilterHeaderLocation, setCurrentFilterHeaderLocation] = useState("");
    const [hideDownloadDialog, setHideDownloadDialog] = useState(false);
    const [headerCenterBtns, setHeaderCenterBtns] = useState([]);

    const userIsLoggedIn = !!localStorage.getItem("token");
    useUnsavedChangesWarning(hasUnsavedChanges, "You have unsaved changes. Do you want to leave this page?");
    // #endregion

    // #region METHODS
    const getUserDataFunction = (
        id,
        currentCompanyID = undefined,
        currentGroupID = undefined,
        redirectToHomepage = false
    ) => {
        const formData = {};
        Library.makeApiRequest({
            endpoint: "Accounts/getUserData",
            data: formData,
            setLoader: setIsLoadingApp
        }).then(res => {
            if (res.data.status === 200) {
                if (localStorage.getItem("isLoggedIn") !== "true") {
                    console.log("User not logged in, cancelling!");
                    return;
                }

                setShowMasterLayout(true);
                const data = res.data.data;

                setUserData(data);

                //const status = data?.BOStagesAccount?.status;
                //const billingPlan = data?.currentCompany?.billingPlan;
                const userName =
                    data?.BOStagesAccount?.firstName +
                    (data?.BOStagesAccount?.lastName ? " " + data?.BOStagesAccount?.lastName : "");
                const userEmail = data?.BOStagesAccount?.email;

                const currentCompanyName = data?.currentCompany?.name;
                const currentCompanyID = data?.currentCompany?._id;
                const currentCompanyLogo = data?.currentCompany?.logoURL;

                //const currentCompanyStatus = data?.currentCompany?.status;
                const currentOrganizationStatus = data?.organization?.status;
                const currentOrganizationID = data?.organization?._id;
                const currentOrganizationName = data?.organization?.name;
                const currentOrganizationCountry = data?.organization?.countryValue;
                const currentOrganizationLogo = data?.organization?.mainImageUrl;
                const activeBrands = data?.activeLabelBrands;
                const activeStores = data?.activeStores;
                const activeCompanies = data?.activeCompanies;
                const activeFFCenters = data?.activeFFCenters;
                const activeRetailBrands = data?.activeRetailBrands;
                const NDAaccepted = data?.NDAaccepted ?? false;
                const profilePhoto = data?.BOStagesAccount?.profilePhotoUrl;
                const currentProfileRole = data?.role;
                const subscriptionPlanIDs = data?.organization?.subscriptionPlanIDs;
                const subscriptionPlanName = data?.organization?.subscriptionPlanName;
                const subscriptionPlanStatus = data?.organization?.subscriptionPlanStatus;
                const trialDaysLeft = data?.organization?.trialDaysLeft ?? 0;
                const BOstagesAccountID = data?.BOStagesAccount?._id;
                const activeConnectedBrands = parseInt(data?.activeConnectedBrands) || 0;

                const defaultRetailBrand = data?.retailBrands?.find(item =>
                    item?.defaultBy?.includes(BOstagesAccountID)
                )?._id;
                const currentRetailBrand = data?.currentRetailBrand?._id;

                const defaultLabelBrand = data?.labelBrands?.find(item =>
                    item?.defaultBy?.includes(BOstagesAccountID)
                )?._id;
                //const currentStatus = data?.invite?.status?.status ?? "Enrolling";
                const trialPeriod = trialDaysLeft > 0 ? trialDaysLeft : subscriptionPlanStatus === "Active" ? "" : 0;

                const firstLogin = data?.BOStagesAccount?.firstLoginDone ?? false;
                const appDownloadedChecked = data?.BOStagesAccount?.appDownloadedChecked ?? false;

                localStorage.setItem("currentOrganizationStatus", currentOrganizationStatus || "");
                localStorage.setItem("subscriptionPlanStatus", subscriptionPlanStatus || "");
                localStorage.setItem("defaultRetailBrand", defaultRetailBrand || currentRetailBrand || "");
                localStorage.setItem("defaultLabelBrand", defaultLabelBrand || "");
                localStorage.setItem("userFirstName", data?.BOStagesAccount?.firstName);
                localStorage.setItem("userLastName", data?.BOStagesAccount?.lastName || "-");
                localStorage.setItem("billingPlan", subscriptionPlanName || "");
                localStorage.setItem("trialPeriod", trialPeriod);
                localStorage.setItem("subscriptionPlanIDs", JSON.stringify(subscriptionPlanIDs) || "[]");
                localStorage.setItem("userName", userName);
                localStorage.setItem("userEmail", userEmail);
                localStorage.setItem("currentCompanyName", currentCompanyName);
                localStorage.setItem("currentCompanyID", currentCompanyID);
                localStorage.setItem("currentCompanyLogo", currentCompanyLogo);
                localStorage.setItem("companyHasStripeCustomerID", !!data?.currentCompany?.stripeCustomerID);
                localStorage.setItem("currentOrganizationID", currentOrganizationID);
                localStorage.setItem("currentOrganizationName", currentOrganizationName);
                localStorage.setItem("currentOrganizationCountry", currentOrganizationCountry);
                localStorage.setItem("activeCompanies", activeCompanies ?? "-");
                localStorage.setItem("activeBrands", activeBrands ?? "-");
                localStorage.setItem("activeStores", activeStores ?? "-");
                localStorage.setItem("activeFFCenters", activeFFCenters ?? "-");
                localStorage.setItem("activeRetailBrands", activeRetailBrands ?? "-");
                localStorage.setItem("currentOrganizationName", currentOrganizationName ?? "-");
                localStorage.setItem("NDAaccepted", NDAaccepted ?? false);
                if (profilePhoto) localStorage.setItem("currentProfilePhoto", profilePhoto);
                localStorage.setItem("currentProfileRole", currentProfileRole ?? "--");
                localStorage.setItem("currentOrganizationLogo", currentOrganizationLogo);
                localStorage.setItem("labelBrands", JSON.stringify(data?.labelBrands) ?? "[]");
                localStorage.setItem("retailBrands", JSON.stringify(data?.retailBrands) ?? "[]");
                localStorage.setItem("invite", JSON.stringify(data?.invite));
                localStorage.setItem("BOstagesAccountID", BOstagesAccountID);
                localStorage.setItem("tempRegistrationNumber", data?.currentCompany?.registrationNumber || "");
                localStorage.setItem("stripeAccountID", data?.currentCompany?.stripeAccountID || "");
                localStorage.setItem("activeConnectedBrands", activeConnectedBrands);
                localStorage.setItem("subscriptionPlanTime", data?.organization?.subscriptionPlanTime || "");
                localStorage.setItem("restrictedStoreIDs", JSON.stringify(data?.restrictions?.storeIDs) ?? "[]");
                //redux save userData
                dispatch(setData({ userAppData: data }));
                dispatch(setNavigations({ navigationAccesses: data?.navigationAccesses }));

                const userIsOnboarding = verifyUserIsOnboarding(data?.navigationAccesses) !== "/dashboard";
                let currentStatus = "--";

                if (userIsOnboarding) {
                    currentStatus = data?.invite?.status?.status ?? "Enrolling";
                } else {
                    if (activeConnectedBrands > 0) currentStatus = "Active";
                    else currentStatus = "Enrolled";
                }
                localStorage.setItem("status", currentStatus);

                setFirstLoginDone(firstLogin);
                setShowWelcomePopup(!firstLogin);

                if (isPwaInstalled) setHideDownloadDialog(true);
                else setHideDownloadDialog(appDownloadedChecked);

                if (firstLogin) {
                    setShowDownloadAppPopup(true);
                } else {
                    updateFirstLoginDone(formData);
                }

                //redirect to home (usually after a login success)
                if (redirectToHomepage) {
                    setShowMasterLayout(true);

                    if (redirectURL) {
                        //TODO: REMOVE LATER THIS CONSOLE LOG
                        navigate(redirectURL);
                        setRedirectURL(null);
                        return;
                    }

                    const redirectOnboardingURL = verifyUserIsOnboarding(data?.navigationAccesses);
                    if (redirectOnboardingURL) {
                        navigate(redirectOnboardingURL);
                    }
                }
            } else {
                localStorage.clear();
            }
        });
    };

    const updateFirstLoginDone = formData => {
        Library.makeCommonPostRequest("Users", "updateFirstLoginDone", formData, false).then(res => {});
    };

    const updateAppDownloadedChecked = formData => {
        Library.makeCommonPostRequest("Users", "updateAppDownloadedChecked", formData, false).then(res => {});
    };

    /** This function is handling whether to show the homepage or onboarding page, and checking whether to show the master layout when user is logged in (header, controlbar, footer, etc)
        If the URL is a public access page URL, it will ignore other flows (Examples: /pre-enrollment-invited, /pre-enrollment, /login (forcing logout url)) **/
    const handleAppHomepageFlow = () => {
        const token = localStorage.getItem("token");

        //if the URL is an public access page URL, then don't show the master layout and clear login and local storage
        if (isPublicAccessPageURL) {
            localStorage.clear();
            setShowMasterLayout(false);
            return;
        } else if (!showMasterLayout) {
            //if the master layout is undefined, we cannot proceed until it is defined
            return;
        }
        //else if there is not a token and the URL is not root page, then don't show the master layout and clear login and local storage (will show login page)
        else if (!token && path !== "/") {
            setShowMasterLayout(false);
            localStorage.clear();
            navigate("/login");
            return;
        }
        //else, handle redirect to homepage or onboarding page
        else if (path === "/" && token) {
            const redirectOnboardingURL = verifyUserIsOnboarding(navigations?.navigationAccesses);

            if (!redirectOnboardingURL) return;
            if (redirectOnboardingURL !== "/" && redirectOnboardingURL !== path) {
                navigate(redirectOnboardingURL);
            }
        }
    };

    const shouldShowMobileWarning = () => {
        const path = location.pathname;
        const exceptionURL = path.startsWith("/pre-enrollment-invited") || path.startsWith("/pre-enrollment");

        return isMobile && !exceptionURL;
    };

    const getRouteName = () => {
        const path = location.pathname;
        const routes = path.split("/");
        return routes[1];
    };

    const logoutIfNoToken = () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setShowMasterLayout(false);
            navigate("/login");
        }
    };

    const handleHideDownloadDialog = isChecked => {
        if (isChecked) updateAppDownloadedChecked({ BOstagesAccountID: localStorage.getItem("BOstagesAccountID") });
    };

    const editAccessAndOnboardedUser = editAccess && hasUserCompletedOnboarding(navigations?.navigationAccesses);

    const refreshHeaderButtons = () => {
        setHeaderCenterBtns([
            {
                title: "EXPORT RULES",
                disabled: !editAccessAndOnboardedUser,
                to: editAccessAndOnboardedUser ? "/products/pos-export" : "#"
            },
            {
                title: "MY DEFAULTS",
                disabled: !editAccessAndOnboardedUser,
                to: editAccessAndOnboardedUser ? "/products/my-defaults/seasons" : "#"
            },
            {
                title: "ORDERS",
                disabled: !editAccessAndOnboardedUser,
                to: editAccessAndOnboardedUser ? "/orders/" : "#"
            },
            {
                title: "REORDERS",
                disabled: !editAccessAndOnboardedUser,
                to: editAccessAndOnboardedUser ? "/orders/reorders" : "#"
            }
        ]);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        try {
            const storedToast = sessionStorage.getItem(PENDING_BILLING_ACTIVATION_TOAST_KEY);
            if (!storedToast) return;

            sessionStorage.removeItem(PENDING_BILLING_ACTIVATION_TOAST_KEY);
            const toast = JSON.parse(storedToast);
            if (toast?.title) Library.showToastMessage({ ...toast, type: "success" });
        } catch {
            // Ignore unavailable storage or malformed one-time toast payloads.
        }
    }, []);

    useEffect(() => {
        if (blocker.state === "blocked" && hasUnsavedChanges) {
            if (!userIsLoggedIn) {
                setHasUnsavedChanges(false);
                setShowLogoutPopup(false);
                blocker.reset();

                localStorage.clear();
            } else {
                setShowUnsavedChangesPopup(true);
            }
        }
        //else console.log("State: ", blocker.state);
    }, [blocker.state]); // Handle unsaved changes blocker

    useEffect(() => {
        if (!showMasterLayout) {
            //if the master layout is undefined, we cannot proceed until it is defined
            return;
        }

        const path = location.pathname;
        const navigationBarMenuBtns = navigationBar(path, navigations);
        setNavigationBarMenuBtns(navigationBarMenuBtns);

        if (currentFilterHeaderLocation !== location.pathname) return;

        //check GET params for layoutType:
        const urlParams = new URLSearchParams(window.location.search);
        const layoutTypeParam = urlParams.get("layoutType");

        if (!layoutTypeParam && filterHeaderProperties?.gridEnabled) setLayoutType("Grid");
        else if (layoutTypeParam === "list") {
            setLayoutType("List");
        }

        handleAppHomepageFlow();
    }, [location, navigations, showMasterLayout, currentFilterHeaderLocation]); // handles master layout and app homepage flow

    useEffect(() => {
        setCurrentFilterHeaderLocation(location.pathname);
    }, [filterHeaderProperties]);

    useEffect(() => {
        if (!userIsLoggedIn) setShowUnsavedChangesPopup(false);
    }, [userIsLoggedIn]);

    useEffect(() => {
        const token = localStorage.getItem("token");

        if (!token) {
            setShowMasterLayout(false);

            if (!isPublicAccessPageURL) {
                setRedirectURL(location.pathname);
                navigate("/login");
            }
            return;
        }

        const interval = setInterval(logoutIfNoToken, 2000);
        return () => clearInterval(interval);
    }, [localStorage.getItem("token"), location.pathname]); // Login check and logs out if no token

    useEffect(() => {
        // Get user data on app start if token exists
        if (localStorage.getItem("token")) {
            getUserDataFunction(
                localStorage.getItem("token"),
                localStorage.getItem("currentCompanyID"),
                localStorage.getItem("currentGroupID")
            );
        }

        // Detect browser type
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes("edg/")) {
            // Updated to detect Edge more reliably
            setBrowser("edge");
        } else if (userAgent.includes("chrome") && !userAgent.includes("edg/")) {
            // Ensure it's Chrome and not Edge
            setBrowser("chrome");
        } else if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
            // Detect Safari browser
            setBrowser("safari");
        }

        // Redirect if on old domain
        if (window.location.hostname === "retailerr.bostages.com") {
            const path = window.location.pathname;
            window.location.href = `https://retailer.bostages.com${path}`;
        }

        // PWA installation detection
        const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
        setIsPwaInstalled(isStandalone);
    }, []); // Runs on start, has getUserData call and others

    useEffect(() => {
        if (!getUserDataToken) return;

        getUserDataFunction(getUserDataToken, undefined, undefined, true);
        setGetUserDataToken(null);
    }, [getUserDataToken]); // Calls getUserDataFunction when getUserDataToken changes

    useEffect(() => {
        if (!refreshUserData) return;
        setRefreshUserData(false);
        const token = localStorage.getItem("token");
        if (!token) {
            console.log("No token found to refresh user data");
            return;
        }
        getUserDataFunction(token, undefined, undefined, false);
    }, [refreshUserData]); // Calls getUserDataFunction when refreshUserData changes

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 1200) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []); // Detects mobile view based on window size

    useEffect(() => {
        if (isPwaInstalled) {
            setHideDownloadDialog(true);
        }
    }, [isPwaInstalled]); // Hide download dialog if PWA is installed

    useEffect(() => {
        refreshHeaderButtons();

        // if onboarding on Billing page then disables AI Chat
        if (verifyUserIsOnboarding(navigations?.navigationAccesses) === "/billing") setAiChatIsDisabled(true);
        else setAiChatIsDisabled(false); //TODO: IN THE FUTURE CHECK IF USER CAN ACCESS AI CHAT BASED ON BILLING PLAN
    }, [editAccess, navigations]); // Refresh header buttons when navigations change

    useEffect(() => {
        const isDashboardPage = path === "/dashboard";

        // show chat window only if not on public access page and not on mobile
        const isChatWindowVisible = !isPublicAccessPageURL && !shouldShowMobileWarning() && !isDashboardPage;

        setAiChatIsVisible(isChatWindowVisible);
    }, [isPublicAccessPageURL, shouldShowMobileWarning, setAiChatIsVisible, location.pathname, isMobile]); // Detect chat window visibility
    // #endregion

    // #region APP PROPS
    const MainLayoutProps = {
        setHeaderCenterBtns: setHeaderCenterBtns,
        setControlBarLeftBtns: setControlBarLeftBtns,
        setControlBarCenterBtns: setControlBarCenterBtns,
        setControlBarRightBtns: setControlBarRightBtns,
        setFilterHeaderProperties: setFilterHeaderProperties,
        filterHeaderProperties: filterHeaderProperties,
        setRefreshUserData: setGetUserDataToken //TODO: LISTEN TO THIS AND REFRESH USER DATA
        //getUserDataFunction: getUserDataFunction //TODO: THIS WILL BE REPLACED BY setRefreshUserData
    };
    // #endregion

    return (
        <div className={`AppContainer route-${getRouteName()}`} style={{ overflow: "hidden", position: "relative" }}>
            <LoadComponents
                shouldShowMobileWarning={shouldShowMobileWarning}
                showTAC={showTAC}
                setShowTAC={setShowTAC}
                handleHideDownloadDialog={handleHideDownloadDialog}
                blocker={blocker}
                browser={browser}
                hideDownloadDialog={hideDownloadDialog}
                userIsLoggedIn={userIsLoggedIn}
                isLoadingApp={isLoadingApp}
                showPopup1={showPopup1}
                showPopup2={showPopup2}
                path={path}
            />

            <WorkspaceLoader
                className={shouldShowMobileWarning() ? "d-none" : ""}
                isLoading={isLoadingApp}
                isLoadingOverlay={false}>
                <QueryClientProvider client={AppQueryClient}>
                    <Suspense fallback={<PreLoader />}>
                        {showMasterLayout ? (
                            <div className="app-main-wrapper premium-app-shell">
                                <div
                                    className="master-layout"
                                    style={{
                                        width: aiChatSidePanelOpen ? "calc(100% - 240px)" : "100%"
                                    }}>
                                    <div className="main-wrapper">
                                        <PopupManager />

                                        <div className="navigation-container">
                                            <NavigationPanel menuBtns={navigationBarMenuBtns} />
                                        </div>

                                        <div className="main-content">
                                            <div className="header-container">
                                                <Header
                                                    centerBtns={headerCenterBtns}
                                                    getUserDataFunction={getUserDataFunction}
                                                />
                                            </div>

                                            <div className="controlbar-container">
                                                <ControlBar
                                                    leftBtns={controlBarLeftBtns}
                                                    centerBtns={controlBarCenterBtns}
                                                    rigthBtns={controlBarRigthBtns}></ControlBar>
                                            </div>

                                            {filterHeaderProperties?.customContentHeader &&
                                                filterHeaderProperties.customContentHeader}

                                            <div className="content router-container library-scrollable">
                                                <Outlet context={{ MainLayoutProps, layoutType }} />
                                            </div>
                                        </div>

                                        {(location?.pathname?.startsWith("/boimages/create") ||
                                            location?.pathname?.startsWith("/boimages/edit")) && <ProductsSidePanel />}
                                    </div>

                                    <div className="footer-wrapper">
                                        <Footer setShowTAC={setShowTAC} />
                                    </div>
                                </div>

                                <ChatWindowDrawer />
                            </div>
                        ) : (
                            <Outlet context={{ MainLayoutProps, layoutType }} />
                        )}
                    </Suspense>
                </QueryClientProvider>
            </WorkspaceLoader>
        </div>
    );
}

// #region COMPONENTS
const LoadComponents = props => {
    // #region STATES
    const {
        shouldShowMobileWarning,
        showTAC,
        setShowTAC,
        handleHideDownloadDialog,
        blocker,
        browser,
        hideDownloadDialog,
        userIsLoggedIn,
        isLoadingApp,
        showPopup1,
        showPopup2,
        path
    } = props;

    const {
        hasUnsavedChanges,
        setHasUnsavedChanges,
        showUnsavedChangesPopup,
        setShowUnsavedChangesPopup,
        showWelcomePopup,
        showDownloadAppPopup
    } = useContext(AppContext);
    const { setShowLogoutPopup } = useContext(LayoutContext);
    // #endregion

    // #region COMPUTED VARIABLES
    const showDownloadDialog = GLOBALS.disableDownloadAppPopup
        ? false
        : (showPopup1 && !shouldShowMobileWarning()) ||
          (["chrome", "edge", "safari"].includes(browser) &&
              !hideDownloadDialog &&
              !showWelcomePopup &&
              userIsLoggedIn &&
              showDownloadAppPopup &&
              !isLoadingApp);

    const showWelcomeDialog = GLOBALS.disableWelcomePopup
        ? false
        : (showPopup2 && !shouldShowMobileWarning()) || (showWelcomePopup && userIsLoggedIn && !isLoadingApp);

    const getOverridingUnsavedChangesProps = () => {
        if (path.startsWith("/stores/pos")) {
            return {
                title: "POS setup",
                message:
                    "You are leaving the POS setup form. BOtag cannot connect to your stores and no data will be exchanged until you set your POS to connect with BOtag.<br/>Please confirm you want to cancel and handle setup later.",
                heading: "Attention!",
                headingColor: "var(--text-icons-orange-500)",
                showCancelButton: true,
                showConfirmButton: true,
                cancelText: "GO BACK",
                confirmText: "CANCEL SETUP",
                cancelState: "secondary",
                confirmState: "secondary"
            };
        }
        if (path.startsWith("/products/pos-export")) {
            return {
                title: "Leaving without saving?",
                message: "If you leave now, you’ll lose the rules you have already set.",
                heading: "ATTENTION!!",
                headingColor: "var(--text-icons-orange-500)",
                showCancelButton: true,
                showConfirmButton: true,
                cancelText: "CANCEL",
                confirmText: "LEAVE",
                cancelState: "secondary",
                confirmState: "secondary",
                confirmTextColor: "var(--text-icons-orange-500)",
                useIconSrc: <AlertTriangle width={40} height={40} color="var(--text-icons-orange-500)" />
            };
        }

        return {
            title: "Abandon editing?",
            message: "Your changes will not be saved.",
            heading: "Just checking...",
            headingColor: "var(--text-icons-orange-500)"
        };
    };
    // #endregion

    return (
        <>
            {/* process.env.REACT_APP_IS_TESTING && (<ReactQueryDevtools initialIsOpen={false} position="bottom-left" />) */}

            <NoInternetMarker />

            <ToastContainer />

            <ChatComponent />

            {shouldShowMobileWarning() && <MobileNotAvailable />}

            {(showTAC?.TermsAndPrivacyPanel || showTAC?.PrivacyPolicy) && (
                <TermsAndPrivacyPanel
                    type={showTAC?.TermsAndPrivacyPanel ? "terms" : "privacy"}
                    onCloseClicked={() => {
                        setShowTAC({
                            TermsAndPrivacyPanel: false,
                            PrivacyPolicy: false
                        });
                    }}
                />
            )}

            <FullScreenOverlay show={showWelcomeDialog}>
                <WelcomeDialog />
            </FullScreenOverlay>

            <FullScreenOverlay show={showDownloadDialog}>
                <DownloadAndInstallAppDialog browser={browser} onHideDialog={handleHideDownloadDialog} />
            </FullScreenOverlay>

            <FullScreenOverlay show={!!showUnsavedChangesPopup}>
                <DialogBoxComponent
                    title="Abandon editing?"
                    message="Your changes will not be saved."
                    heading="Attention!"
                    headingColor="var(--text-icons-orange-500)"
                    onClickYes={() => {
                        setShowUnsavedChangesPopup(false);
                        if (hasUnsavedChanges) {
                            setHasUnsavedChanges(false);
                            blocker.proceed();
                        }
                    }}
                    onClickNo={() => {
                        setShowLogoutPopup(false);
                        setShowUnsavedChangesPopup(false);
                        if (hasUnsavedChanges) blocker.reset();
                    }}
                    {...getOverridingUnsavedChangesProps()}
                />
            </FullScreenOverlay>
        </>
    );
};

const ChatWindowDrawer = () => {
    // #region VARS
    const { aiChatSidePanelOpen, setAiChatSidePanelOpen } = useContext(AppContext);
    const resizeStartRef = useRef(null);
    // #endregion

    // #region STATES
    const [drawerWidth, setDrawerWidth] = useState(CHAT_DRAWER_MIN_WIDTH);
    const [isResizing, setIsResizing] = useState(false);
    // #endregion

    // #region METHODS
    const handleResizeStart = event => {
        if (event.button !== 0) return;

        event.preventDefault();
        resizeStartRef.current = {
            pointerID: event.pointerId,
            pointerX: event.clientX,
            drawerWidth
        };
        setIsResizing(true);
    };

    const handleResizeKeyDown = event => {
        let widthChange = 0;

        if (event.key === "ArrowLeft") widthChange = CHAT_DRAWER_KEYBOARD_STEP;
        else if (event.key === "ArrowRight") widthChange = -CHAT_DRAWER_KEYBOARD_STEP;
        else if (event.key === "Home") widthChange = CHAT_DRAWER_MIN_WIDTH - drawerWidth;
        else if (event.key === "End") widthChange = CHAT_DRAWER_MAX_WIDTH - drawerWidth;
        else return;

        event.preventDefault();
        setDrawerWidth(currentWidth =>
            Math.min(CHAT_DRAWER_MAX_WIDTH, Math.max(CHAT_DRAWER_MIN_WIDTH, currentWidth + widthChange))
        );
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        if (!isResizing) return undefined;

        const previousCursor = document.body.style.cursor;
        const previousUserSelect = document.body.style.userSelect;
        document.body.style.cursor = "ew-resize";
        document.body.style.userSelect = "none";

        const handleResize = event => {
            const resizeStart = resizeStartRef.current;
            if (!resizeStart || event.pointerId !== resizeStart.pointerID) return;

            const nextWidth = resizeStart.drawerWidth + resizeStart.pointerX - event.clientX;
            setDrawerWidth(Math.min(CHAT_DRAWER_MAX_WIDTH, Math.max(CHAT_DRAWER_MIN_WIDTH, nextWidth)));
        };

        const handleResizeEnd = event => {
            if (event?.pointerId !== undefined && event.pointerId !== resizeStartRef.current?.pointerID) return;

            resizeStartRef.current = null;
            setIsResizing(false);
        };

        window.addEventListener("pointermove", handleResize);
        window.addEventListener("pointerup", handleResizeEnd);
        window.addEventListener("pointercancel", handleResizeEnd);
        window.addEventListener("blur", handleResizeEnd);

        return () => {
            window.removeEventListener("pointermove", handleResize);
            window.removeEventListener("pointerup", handleResizeEnd);
            window.removeEventListener("pointercancel", handleResizeEnd);
            window.removeEventListener("blur", handleResizeEnd);
            document.body.style.cursor = previousCursor;
            document.body.style.userSelect = previousUserSelect;
        };
    }, [isResizing]);
    // #endregion

    return (
        <Drawer
            anchor="right"
            variant="persistent"
            open={aiChatSidePanelOpen}
            onClose={() => setAiChatSidePanelOpen(false)}
            sx={{
                "& .MuiPaper-root": {
                    backgroundColor: "var(--backgrounds-lines-n-600)"
                },
                "&": {
                    maxHeight: "100vh"
                }
            }}
            PaperProps={{
                style: {
                    position: "relative",
                    transition: isResizing ? "none" : "width 0.3s",
                    width: aiChatSidePanelOpen ? drawerWidth : 0,
                    zIndex: 9999
                }
            }}>
            {aiChatSidePanelOpen && (
                <StyledChatDrawerResizeHandle
                    role="separator"
                    aria-label="Resize NanaBO side panel"
                    aria-orientation="vertical"
                    aria-valuemin={CHAT_DRAWER_MIN_WIDTH}
                    aria-valuemax={CHAT_DRAWER_MAX_WIDTH}
                    aria-valuenow={drawerWidth}
                    tabIndex={0}
                    onPointerDown={handleResizeStart}
                    onKeyDown={handleResizeKeyDown}
                />
            )}
            <ChatWindow isSidePanelFullScreen={true} />
        </Drawer>
    );
};
// #endregion
